"use client"

import { FileText, Video, BookOpen, FileSpreadsheet, Presentation, File, Download, Eye } from "lucide-react"

export interface Resource {
  id: string
  title: string
  type: string
  targetClass: string
  uploadedBy: string
  uploadDate: string
  downloads: number
  size: string
}

interface ResourcesTableProps {
  resources: Resource[]
}

export function ResourcesTable({ resources }: ResourcesTableProps) {
  const getResourceIcon = (type: string) => {
    switch (type) {
      case "PDF":
        return <FileText className="w-5 h-5 text-red-500" />
      case "Video":
        return <Video className="w-5 h-5 text-purple-500" />
      case "Quiz":
        return <BookOpen className="w-5 h-5 text-blue-500" />
      case "Worksheet":
        return <FileSpreadsheet className="w-5 h-5 text-green-500" />
      case "Presentation":
        return <Presentation className="w-5 h-5 text-orange-500" />
      default:
        return <File className="w-5 h-5 text-gray-500" />
    }
  }

  const getTypeBadgeColor = (type: string) => {
    switch (type) {
      case "PDF":
        return "bg-red-50 text-red-700 border-red-200"
      case "Video":
        return "bg-purple-50 text-purple-700 border-purple-200"
      case "Quiz":
        return "bg-blue-50 text-blue-700 border-blue-200"
      case "Worksheet":
        return "bg-green-50 text-green-700 border-green-200"
      case "Presentation":
        return "bg-orange-50 text-orange-700 border-orange-200"
      default:
        return "bg-gray-50 text-gray-700 border-gray-200"
    }
  }

  const handleDownload = (resource: Resource) => {
    console.log("Downloading resource:", resource.id, resource.title)
  }

  const handleView = (resource: Resource) => {
    console.log("Viewing resource:", resource.id, resource.title)
  }

  if (resources.length === 0) {
    return (
      <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-200 text-center">
        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No Resources Found</h3>
        <p className="text-gray-500">Try adjusting your filters or add a new resource</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Resource
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Type</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Target Class
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Uploaded By
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Date</th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Downloads
              </th>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Size</th>
              <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {resources.map((resource) => (
              <tr key={resource.id} className="hover:bg-gray-50 transition">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    {getResourceIcon(resource.type)}
                    <div>
                      <p className="font-medium text-gray-900">{resource.title}</p>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getTypeBadgeColor(
                      resource.type,
                    )}`}
                  >
                    {resource.type}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-700">{resource.targetClass}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-700">{resource.uploadedBy}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-500">{resource.uploadDate}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-700">{resource.downloads}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="text-sm text-gray-500">{resource.size}</span>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleView(resource)}
                      className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                      title="View"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDownload(resource)}
                      className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition"
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
